import React, { useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { WalletPaymentData, WalletAdvance } from '../../collectPayment.types';
import { formatCurrency } from '../../collectPayment.helper';
import { WALLET_TABLE_COLUMNS } from '../../collectPayment.constants';
import { TABLE_HEADER_ROW_SX } from '../../../../../utils/typographyStyles';
import useCurrency, { getCurrencySymbol } from '../../../../../components/CurrencyProvider';
import { CHECKED_ICON, UNCHECKED_ICON } from '../../../Invoice/common/InvoiceTableCommon';

interface WalletPaymentFormProps {
  data: WalletPaymentData;
  onChange: (data: WalletPaymentData) => void;
  walletBalance: number;
  advances: WalletAdvance[];
  currency?: string;
  error?: string;
  /** Remaining amount to pay (caps the max amount user can enter) */
  remainingAmount?: number;
  /** Credit transaction IDs already added to payment history */
  usedCreditTransactionIds?: number[];
  /** Callback to remove a credit transaction from payment history */
  onRemoveFromHistory?: (creditTransactionId: number) => void;
}

const WalletPaymentForm: React.FC<WalletPaymentFormProps> = ({
  data,
  onChange,
  walletBalance,
  advances,
  currency: currencyProp,
  error,
  remainingAmount = Infinity,
  usedCreditTransactionIds = [],
  onRemoveFromHistory,
}) => {
  const { currencySymbol: contextCurrencySymbol, currency: contextCurrency } = useCurrency();
  // Use passed currency prop with fallback to context value
  const currency = currencyProp ?? contextCurrency;
  const currencySymbol = currencyProp ? getCurrencySymbol(currencyProp) : contextCurrencySymbol;
  const prevRemainingRef = useRef(remainingAmount);
  const onChangeRef = useRef(onChange);

  // Keep refs updated with latest values
  onChangeRef.current = onChange;

  const handleAdvanceToggle = (advanceId: string | number, creditTransactionId?: number) => {
    // Find the advance being toggled
    const advanceToToggle = data.advances.find((adv) => adv.id === advanceId);
    
    // If the advance is already in payment history and user is unchecking, remove from history
    if (advanceToToggle?.isSelected && creditTransactionId && usedCreditTransactionIds.includes(creditTransactionId)) {
      onRemoveFromHistory?.(creditTransactionId);
    }
    
    const updatedAdvances = data.advances.map((adv) => {
      if (adv.id === advanceId) {
        const isNowSelected = !adv.isSelected;
        // When checking, auto-fill with requiredAmount (or balance if not set)
        // When unchecking, reset to 0
        const newConsumeAmount = isNowSelected 
          ? (adv.requiredAmount ?? adv.balance) 
          : 0;
        return { ...adv, isSelected: isNowSelected, consumeAmount: newConsumeAmount };
      }
      return adv;
    });
    
    const totalAmount = updatedAdvances
      .filter((a) => a.isSelected)
      .reduce((sum, a) => sum + a.consumeAmount, 0);

    onChange({ ...data, advances: updatedAdvances, amount: totalAmount });
  };

  const handleConsumeAmountChange = (advanceId: string | number, amount: number) => {
    const updatedAdvances = data.advances.map((adv) =>
      adv.id === advanceId
        ? { ...adv, consumeAmount: Math.min(amount, adv.balance) }
        : adv
    );

    const totalAmount = updatedAdvances
      .filter((a) => a.isSelected)
      .reduce((sum, a) => sum + a.consumeAmount, 0);

    onChange({ ...data, advances: updatedAdvances, amount: totalAmount });
  };

  useEffect(() => {
    if (advances.length > 0 && data.advances.length === 0) {
      onChange({ ...data, advances: advances });
    }
  }, [advances, data, onChange]);

  // Calculate remaining available balance after selected consume amounts
  const totalConsumed = (data.advances.length > 0 ? data.advances : advances)
    .filter((a) => a.isSelected)
    .reduce((sum, a) => sum + (a.consumeAmount || 0), 0);
  const remainingBalance = walletBalance - totalConsumed;
  
  // Check if we have advances to select from, or just a simple balance
  const hasAdvancesToSelect = data.advances.length > 0 || advances.length > 0;
  
  // Max amount is the minimum of wallet balance and remaining amount
  const maxAllowedAmount = Math.min(walletBalance, remainingAmount);

  // Auto-populate amount when no advances table (direct wallet balance payment)
  useEffect(() => {
    const remainingChanged = prevRemainingRef.current !== remainingAmount;
    prevRemainingRef.current = remainingAmount;
    
    // Only auto-populate when there's no advances table
    if (!hasAdvancesToSelect && maxAllowedAmount > 0) {
      // Auto-populate on first render when amount is 0
      if (!data.amount) {
        onChangeRef.current({ ...data, amount: maxAllowedAmount });
      }
      // Or when remaining amount changes and current amount is 0
      else if (remainingChanged && data.amount === 0) {
        onChangeRef.current({ ...data, amount: maxAllowedAmount });
      }
    }
  }, [remainingAmount, maxAllowedAmount, hasAdvancesToSelect, data]);
  
  // Handle direct amount entry when no advances are available
  const handleDirectAmountChange = (amount: number) => {
    const cappedAmount = Math.min(amount, maxAllowedAmount);
    onChange({ ...data, amount: cappedAmount });
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          px: 2,
          py: 1,
          mb: 2,
        }}
      >
        <AccountBalanceWalletOutlinedIcon sx={{ color: '#757575', fontSize: 20 }} />
        <Box>
          <Typography variant="caption" color="text.secondary">
            Available Balance
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatCurrency(remainingBalance, currency)}
          </Typography>
        </Box>
      </Box>

      {(data.advances.length > 0 || advances.length > 0) && (
        <TableContainer component={Paper} elevation={0} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={TABLE_HEADER_ROW_SX}>
                {WALLET_TABLE_COLUMNS.map((col) => (
                  <TableCell key={col.id} sx={{ width: col.width }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.advances.length > 0 ? data.advances : advances).map((advance, index) => {
                // Calculate running used amount: original used + sum of consumeAmounts from previous selected rows
                const advancesList = data.advances.length > 0 ? data.advances : advances;
                const previousConsumed = advancesList
                  .slice(0, index)
                  .filter((a) => a.isSelected)
                  .reduce((sum, a) => sum + (a.consumeAmount || 0), 0);
                const runningUsed = advance.used + previousConsumed;
                const runningBalance = advance.total - runningUsed;
                
                // Check if this advance's credit transaction is already used in payment history
                const isAlreadyUsed = advance.creditTransactionId 
                  ? usedCreditTransactionIds.includes(advance.creditTransactionId)
                  : false;
                
                return (
                  <TableRow key={advance.id} sx={{ opacity: isAlreadyUsed ? 0.5 : 1 }}>
                    <TableCell sx={{ fontSize: '13px' }}>{advance.date}</TableCell>
                    <TableCell sx={{ fontSize: '13px' }}>
                      <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
                        {advance.advanceAgainst || advance.packageName}
                      </Typography>
                      {advance.packageName && advance.advanceAgainst && advance.packageName !== advance.advanceAgainst && (
                        <Typography variant="caption" sx={{ fontSize: '11px', color: '#666' }}>
                          ({advance.packageName})
                        </Typography>
                      )}
                      {isAlreadyUsed && (
                        <Typography variant="caption" sx={{ fontSize: '10px', color: '#d32f2f' }}>
                          Already added
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px' }}>{formatCurrency(advance.total, currency).replace(currencySymbol, '')}</TableCell>
                    <TableCell sx={{ fontSize: '13px' }}>{formatCurrency(runningUsed, currency).replace(currencySymbol, '')}</TableCell>
                    <TableCell sx={{ fontSize: '13px' }}>{formatCurrency(runningBalance, currency).replace(currencySymbol, '')}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          size="small"
                          checked={advance.isSelected}
                          onChange={() => handleAdvanceToggle(advance.id, advance.creditTransactionId)}
                          icon={UNCHECKED_ICON}
                          checkedIcon={CHECKED_ICON}
                        />
                        <TextField
                          size="small"
                          type="number"
                          value={advance.consumeAmount || ''}
                          onChange={(e) => handleConsumeAmountChange(advance.id, parseFloat(e.target.value) || 0)}
                          disabled={!advance.isSelected || isAlreadyUsed}
                          placeholder="0.00"
                          sx={{ width: 140 }}
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Amount"
          type="number"
          value={data.amount || ''}
          disabled={hasAdvancesToSelect}
          onChange={(e) => handleDirectAmountChange(parseFloat(e.target.value) || 0)}
          error={!!error}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
            },
          }}
          helperText={error || (!hasAdvancesToSelect && maxAllowedAmount > 0 ? `Max: ${formatCurrency(maxAllowedAmount, currency)}` : undefined)}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Notes"
          multiline
          minRows={2}
          maxRows={4}
          value={data.notes || ''}
          onChange={(e) => onChange({ ...data, notes: e.target.value })}
          placeholder="Enter Note"
        />
      </Box>
    </Box>
  );
};

export default WalletPaymentForm;
